import { useCallback, useEffect, useState } from 'react';

/**
 * Tema claro/escuro do shell.
 *
 * O `tailwind.config.js` já declarava `darkMode: 'class'` e o `index.html` já
 * marcava `<html class="light">`, mas não havia toggle nem nenhuma regra `dark:`
 * em uso — a declaração existia sem efeito. Este hook fecha isso.
 *
 * Mantém as duas convenções em sincronia:
 *  - `class="dark"` / `class="light"` no `<html>`, que é o que o Tailwind lê;
 *  - `data-ep-theme`, que é o que o CSS do shell usa para trocar as custom
 *    properties (mesma convenção do protótipo em base-html/app/assets/app.css).
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'nexora-theme';

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    // modo privado / storage bloqueado: cai no padrão claro
    return null;
  }
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute('data-ep-theme', theme);
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(() => readStored() ?? 'light');

  useEffect(() => {
    apply(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // sem persistência é aceitável; o tema vale para a sessão
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, toggle };
}
